import {
  Grid,
  Box,
  Typography,
  Chip,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Button,
} from "@mui/material";

import ArticleRoundedIcon from "@mui/icons-material/ArticleRounded";
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";
import FolderOpenRoundedIcon from "@mui/icons-material/FolderOpenRounded";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";

import { useEffect, useState, useCallback } from "react";
import DatagridCustom from "../General/DatagridCustom";
import AppDialog from "../General/Dialog/AppDialog";
import PreviewDialog from "../General/Dialog/PreviewDialog";
import ConfirmDialog from "../General/Dialog/ConfirmDialog";
import UploadLegalFileDocumentDialog from "./UploadLegalFileDocumentDialog";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";

import {
  getLegalFileDocuments,
  downloadLegalFileDocument,
  deleteLegalFileDocument,
} from "../../services/LegalFileAttachmentService";

function formatSize(bytes) {
  if (!bytes) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDate(raw) {
  const date = new Date(raw);
  if (isNaN(date.getTime())) return "—";
  const p = (n) => String(n).padStart(2, "0");
  return `${p(date.getDate())}/${p(date.getMonth() + 1)}/${date.getFullYear()} ${p(date.getHours())}:${p(date.getMinutes())}`;
}

export default function ManagerLegalFileDocument() {
  const { showToast } = useToast();
  const [rows, setRows]               = useState([]);
  const [total, setTotal]             = useState(0);
  const [loading, setLoading]         = useState(false);

  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
  const [filterModel, setFilterModel]         = useState({ items: [], quickFilterValues: [] });

  // preview
  const [openPreview, setOpenPreview]       = useState(false);
  const [previewUrl, setPreviewUrl]         = useState(null);
  const [previewTitle, setPreviewTitle]     = useState("");
  const [previewLoading, setPreviewLoading] = useState(false);

  // modal expedientes
  const [openExpedientes, setOpenExpedientes]   = useState(false);
  const [expedientesData, setExpedientesData]   = useState({ name: "", files: [] });

  // carga / borrado
  const [openUpload, setOpenUpload]       = useState(false);
  const [rowToDelete, setRowToDelete]     = useState(null);
  const [deleting, setDeleting]           = useState(false);

  const { hasPermission } = useAuth();

  const fetchDocuments = useCallback(async () => {
    setLoading(true);
    try {
      const search = filterModel.quickFilterValues?.join(" ") || "";
      const response = await getLegalFileDocuments({
        page: paginationModel.page + 1,
        perPage: paginationModel.pageSize,
        search,
      });
      setRows(response.data.data);
      setTotal(response.data.total);
    } catch {
      showToast("No se pudieron cargar los adjuntos.", "error")
    } finally {
      setLoading(false);
    }
  }, [paginationModel, filterModel, showToast]);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  const handlePreview = async (row) => {
    setPreviewTitle(row.original_name || row.name);
    setPreviewUrl(null);
    setPreviewLoading(true);
    setOpenPreview(true);
    try {
      const response = await downloadLegalFileDocument(row.id);
      const blob = new Blob([response.data], { type: row.mime || "application/pdf" });
      setPreviewUrl(URL.createObjectURL(blob));
    } catch {
      showToast("No se pudo cargar el adjunto.", "error")
      setOpenPreview(false);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleClosePreview = () => {
    setOpenPreview(false);
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
  };

  const handleDeleteConfirm = async () => {
    setDeleting(true);
    try {
      await deleteLegalFileDocument(rowToDelete.id);
      showToast("Documento eliminado.", "success");
      setRowToDelete(null);
      fetchDocuments();
    } catch (err) {
      showToast(err?.response?.data?.message ?? "No se pudo eliminar el documento.", "error");
    } finally {
      setDeleting(false);
    }
  };

  const handleOpenExpedientes = (row) => {
    setExpedientesData({
      name: row.original_name || row.name,
      files: row.legal_files || [],
    });
    setOpenExpedientes(true);
  };

  const columns = [
    {
      field: "original_name",
      headerName: "Adjunto",
      flex: 2.5,
      valueGetter: (params) => params || "—",
    },
    {
      field: "document_type_label",
      headerName: "Tipo",
      flex: 1.5,
      valueGetter: (params, row) => params || row?.document_type || "—",
    },
    {
      field: "size",
      headerName: "Tamaño",
      flex: 0.9,
      valueGetter: (params) => formatSize(params),
    },
    {
      field: "created_at",
      headerName: "Fecha",
      flex: 1.5,
      valueGetter: (params) => formatDate(params),
    },
    {
      field: "data_fields",
      headerName: "Datos leídos",
      flex: 1.6,
      sortable: false,
      valueGetter: (params, row) =>
        (params ?? []).length
          ? [
              ...params.map((f) => `${f.label}: ${f.value || "falta dato"}`),
              ...(row?.data_warning ? [`⚠ ${row.data_warning}`] : []),
            ].join(" · ")
          : "—",
    },
    {
      field: "uploader",
      headerName: "Cargado por",
      flex: 1.2,
      sortable: false,
      valueGetter: (params) => params?.name || "Sistema",
    },
    {
      field: "legal_files",
      headerName: "Expedientes",
      flex: 1.2,
      sortable: false,
      renderCell: (params) => {
        const files = params.value || [];
        if (!files.length) return <Typography variant="body2" color="text.secondary">—</Typography>;
        return (
          <Chip
            icon={<FolderOpenRoundedIcon />}
            label={`${files.length} expediente${files.length !== 1 ? "s" : ""}`}
            size="small"
            variant="outlined"
            onClick={() => handleOpenExpedientes(params.row)}
            clickable
            sx={{ fontWeight: 700 }}
          />
        );
      },
    },
    {
      field: "actions",
      headerName: "Acciones",
      flex: 0.9,
      sortable: false,
      renderCell: (params) => (
        <Box sx={{ display: "flex", gap: 0.5 }}>
          <IconButton
            color="primary"
            size="small"
            onClick={() => handlePreview(params.row)}
            disabled={!hasPermission("descargar archivos adjuntos expedientes")}
          >
            <VisibilityRoundedIcon fontSize="small" />
          </IconButton>
          {params.row.uploaded_by && hasPermission("eliminar documentos expedientes") && (
            <IconButton color="error" size="small" onClick={() => setRowToDelete(params.row)}>
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
      ),
    },
  ];

  return (
    <Grid container justifyContent="center" sx={{ mt: 4 }}>
      <Grid size={{ xs: 12 }}>

        <DatagridCustom
          rows={rows}
          columns={columns}
          getRowId={(row) => row.id}
          title="Adjuntos de Expedientes"
          description="Adjuntos subidos y sus expedientes relacionados."
          icon={<ArticleRoundedIcon sx={{ fontSize: 32 }} />}
          headerActions={
            hasPermission("cargar documentos expedientes") && (
              <Button
                variant="contained"
                startIcon={<CloudUploadOutlinedIcon />}
                onClick={() => setOpenUpload(true)}
                sx={{ borderRadius: 2, fontWeight: 800, textTransform: "none" }}
              >
                Cargar documentos
              </Button>
            )
          }
          paginationModel={paginationModel}
          onPaginationChange={setPaginationModel}
          filterModel={filterModel}
          onFilterChange={setFilterModel}
          filterMode="server"
          total={total}
          loading={loading}
          isServerPagination={true}
        />
      </Grid>

      <UploadLegalFileDocumentDialog
        open={openUpload}
        onClose={() => setOpenUpload(false)}
        onDone={fetchDocuments}
      />

      <ConfirmDialog
        open={Boolean(rowToDelete)}
        onClose={() => setRowToDelete(null)}
        onConfirm={handleDeleteConfirm}
        title="Eliminar documento"
        description={`¿Eliminar "${rowToDelete?.original_name ?? ""}"? Deja de estar disponible para los escritos.`}
        confirmText="Eliminar"
        confirmColor="error"
        loading={deleting}
      />

      {/* Preview PDF */}
      <PreviewDialog
        open={openPreview}
        onClose={handleClosePreview}
        title={previewTitle}
        url={previewUrl}
        loading={previewLoading}
      />

      {/* Modal expedientes */}
      <AppDialog
        open={openExpedientes}
        onClose={() => setOpenExpedientes(false)}
        fullWidth
        maxWidth="sm"
        fullScreenMobile
        fullScreenBreakpoint="sm"
        stickyHeader
        title={`Expedientes — ${expedientesData.name}`}
      >
        {expedientesData.files.length === 0 ? (
          <Typography sx={{ p: 2 }} color="text.secondary">
            Sin expedientes relacionados.
          </Typography>
        ) : (
          <List dense disablePadding>
            {expedientesData.files.map((lf) => (
              <ListItem
                key={lf.id}
                component="a"
                href={`/legal/file/${lf.id}`}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  borderRadius: 2,
                  mb: 0.5,
                  color: "text.primary",
                  textDecoration: "none",
                  "&:hover": { bgcolor: "action.hover" },
                }}
              >
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    bgcolor: "primary.main",
                    mr: 1.5,
                    flexShrink: 0,
                  }}
                />
                <ListItemText
                  primary={lf.code}
                  primaryTypographyProps={{ fontWeight: 700, fontSize: 14 }}
                />
              </ListItem>
            ))}
          </List>
        )}
      </AppDialog>
    </Grid>
  );
}
